import React, { useEffect, useState } from 'react';
import { Hasher } from '../api/hasher';
import { getDefaultExecutorRole } from '../api/role/utils';
import { Role } from '../api/types';
import { ExecutorRoleScopedErrorBoundary } from '../common/components/errors/boundaries/ExecutorRoleScopedErrorBoundary';
import {
  ExecutorRoleContext,
  ExecutorRoleSetterContext,
  useExecutorRoleFromLocalStorageContext,
  useExecutorRolesContext,
  useLocalStorageExecutorRoleServiceContext,
  useMixpanelContext,
} from '../context/contexts';
import { ExecutorRoleScopedContextProvider } from './ExecutorRoleScopedContextProvider';
import { useMemoWithDeps } from './hooks';

const DebugPrefix = 'ExecutorRoleContextProvider';
export const ExecutorRoleContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const mixpanel = useMixpanelContext();
  const executorRoleFromLocalStorage = useExecutorRoleFromLocalStorageContext();
  const executorRoles = useExecutorRolesContext();

  const executorRole = useMemoWithDeps(
    `${DebugPrefix} - executorRole from local storage or default from executor roles`,
    () =>
      (executorRoleFromLocalStorage &&
        executorRoles.find((r) => r.id === executorRoleFromLocalStorage.id)) ??
      getDefaultExecutorRole(executorRoles),
    [Hasher.hash(executorRoleFromLocalStorage), Hasher.hash(executorRoles)]
  );

  const localStorageExecutorRoleService =
    useLocalStorageExecutorRoleServiceContext();
  const [selectedExecutorRole, setSelectedExecutorRole] =
    useState<Role>(executorRole);

  const handleSetSelectedExecutorRole = (role: Role) => {
    setSelectedExecutorRole(role);
    localStorageExecutorRoleService.set(role);
  };

  useEffect(() => {
    mixpanel.actions.register({ executorRole: selectedExecutorRole });
  }, [mixpanel, selectedExecutorRole]);

  return (
    <ExecutorRoleContext.Provider value={selectedExecutorRole}>
      <ExecutorRoleSetterContext.Provider value={handleSetSelectedExecutorRole}>
        <ExecutorRoleScopedContextProvider>
          <ExecutorRoleScopedErrorBoundary>
            {children}
          </ExecutorRoleScopedErrorBoundary>
        </ExecutorRoleScopedContextProvider>
      </ExecutorRoleSetterContext.Provider>
    </ExecutorRoleContext.Provider>
  );
};
